import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./EditCategory.css";
import {
  getCategoryById,
  updateCategoryApi,
} from "../../../../services/Category/category.service";

import { getAllDesireds } from "../../../../services/DesiredType/DesiredType.service";

import { toast } from "react-toastify";

interface MetalType {
  id: number;
  name: string;
}

const EditCategory: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  const [categoryName, setCategoryName] = useState("");
  const [description, setDescription] = useState("");

  const [metalTypeId, setMetalTypeId] = useState("");

  const [metalTypes, setMetalTypes] = useState<MetalType[]>([]);

  const [imageFile, setImageFile] = useState<File | null>(null);

  const [preview, setPreview] = useState<string | null>(null);

  const [status, setStatus] = useState("active");

  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  // ✅ Fetch Metal Types
  useEffect(() => {
    fetchMetalTypes();
  }, []);

  const fetchMetalTypes = async () => {
    try {
      const response: any = await getAllDesireds();

      setMetalTypes(response?.data || []);
    } catch (error) {
      console.error("Metal type fetch failed", error);
    }
  };

  // ✅ Fetch Category
  useEffect(() => {
    const fetchCategory = async () => {
      if (!id) return;

      try {
        const data = await getCategoryById(Number(id));

        setCategoryName(data.name || "");

        setDescription(data.description || "");

        setStatus(data.status || "active");

        setMetalTypeId(
          data.metalTypeId ? String(data.metalTypeId) : ""
        );

        if (data.image) {
          setPreview(data.image);
        }
      } catch (error) {
        toast.error("Failed to fetch category");
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id]);

  // ✅ Handle Image Change
  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0] || null;

    if (file) {
      setImageFile(file);

      const reader = new FileReader();

      reader.onloadend = () =>
        setPreview(reader.result as string);

      reader.readAsDataURL(file);
    }
  };

  // ✅ Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    if (!metalTypeId) {
      toast.error("Please select metal type");
      return;
    }

    try {
      setSubmitting(true);

      await updateCategoryApi(Number(id), {
        name: categoryName,
        description,
        metalTypeId,
        status: status.toUpperCase(),
        image: imageFile,
      });

      toast.success("Category updated successfully");

      navigate("/category/list");
    } catch (error) {
      toast.error("Failed to update category");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="edit-category-overlay">
      <div className="edit-category-modal">
        <div className="add-category-container">
          <h2>Edit Category</h2>

          <form onSubmit={handleSubmit}>
            <input
              type="text"
              value={categoryName}
              onChange={(e) =>
                setCategoryName(e.target.value)
              }
              placeholder="Category Name"
              required
            />

            {/* ✅ Metal Type Dropdown */}
            <select
              value={metalTypeId}
              onChange={(e) =>
                setMetalTypeId(e.target.value)
              }
              required
            >
              <option value="">
                Select Metal Type
              </option>

              {metalTypes.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={description}
              onChange={(e) =>
                setDescription(e.target.value)
              }
              placeholder="Description"
            />

            <div className="image-upload-box">
              {preview ? (
                <img
                  src={preview}
                  alt="Preview"
                  className="preview-image"
                />
              ) : (
                <span>Click to upload image</span>
              )}

              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>

            <div className="button-close">
              <button
                type="button"
                onClick={() =>
                  navigate("/category/list")
                }
              >
                Close
              </button>

              <button
                type="submit"
                disabled={submitting}
              >
                {submitting
                  ? "Updating..."
                  : "Update Category"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditCategory;